<div class="p-3 h-[calc(100%-var(--spacing)*15)]">
  <app-breadcrumb [items]="[{ label: 'Release Management' }, { label: 'Synchronizer' }]"/>
  <div class="h-[calc(100%-var(--spacing)*9)] rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="h-full w-full overflow-y-scroll">

      <!-- Request -->
      <div class="border-b border-stroke px-5 py-4 dark:border-strokedark sm:px-7.5">
        <h4 class="text-lg font-semibold text-gray-800 dark:text-gray-300">Sync Export Request</h4>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Export an organization's configuration from one environment so it can be imported to another.
        </p>

        <div class="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400">Organization</label>
            <select [(ngModel)]="organizationId"
              class="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white">
              <option value="" disabled>Select organization</option>
              @for (org of organizationStore.organizations(); track org.id) {
                <option [value]="org.id">{{ org.name }}</option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400">Direction</label>
            <div class="flex items-center rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
              @for (direction of directionOptions; track direction.source) {
                <button type="button" (click)="selectDirection(direction)"
                  class="flex items-center gap-1.5 rounded-md px-3.5 py-2 text-sm font-medium uppercase transition-colors"
                  [class.bg-gray-300]="isDirectionSelected(direction)" [class.text-gray-800]="isDirectionSelected(direction)"
                  [class.dark:bg-gray-700]="isDirectionSelected(direction)" [class.dark:text-white]="isDirectionSelected(direction)"
                  [class.text-gray-500]="!isDirectionSelected(direction)" [class.dark:text-gray-400]="!isDirectionSelected(direction)">
                  <span class="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-700">{{ direction.source }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                  </svg>
                  <span class="rounded bg-purple-100 px-1.5 py-0.5 text-xs font-bold text-purple-700">{{ direction.destination }}</span>
                </button>
              }
            </div>
          </div>

          <label class="mb-2 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input type="checkbox" [(ngModel)]="includeGlobalConfigurations" class="h-4 w-4 rounded border-gray-300 dark:border-gray-700"/>
            Include global configurations
          </label>
        </div>

        <div class="mt-4">
          <label class="mb-1.5 block text-xs text-gray-500 dark:text-gray-400">Entities to include</label>
          <div class="flex flex-wrap gap-x-4 gap-y-2">
            @for (entity of entityOptions; track entity) {
              <label class="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300"
                [class.font-semibold]="entity === 'Full'">
                <input type="checkbox" [checked]="isIncluded(entity)" (change)="toggleIncludeEntity(entity)"
                  [disabled]="entity !== 'Full' && isFullSelected()"
                  class="h-4 w-4 rounded border-gray-300 disabled:cursor-not-allowed dark:border-gray-700"/>
                {{ formatEntity(entity) }}
              </label>
            }
          </div>
          @if (isFullSelected()) {
            <p class="mt-1.5 text-xs text-gray-400">Full is selected — every entity is included.</p>
          }
        </div>

        <div class="mt-4">
          <label class="mb-1.5 block text-xs text-gray-500 dark:text-gray-400">
            Entities to exclude
            @if (!isFullSelected()) {
              <span class="font-normal text-gray-400">(only available when Full is selected)</span>
            }
          </label>
          <div class="flex flex-wrap gap-x-4 gap-y-2">
            @for (entity of excludeEntityOptions; track entity) {
              <label class="flex items-center gap-1.5 text-sm"
                [class.text-gray-700]="isFullSelected()" [class.dark:text-gray-300]="isFullSelected()"
                [class.text-gray-400]="!isFullSelected()" [class.dark:text-gray-600]="!isFullSelected()">
                <input type="checkbox" [checked]="isExcluded(entity)" (change)="toggleExcludeEntity(entity)"
                  [disabled]="!isFullSelected()"
                  class="h-4 w-4 rounded border-gray-300 disabled:cursor-not-allowed dark:border-gray-700"/>
                {{ formatEntity(entity) }}
              </label>
            }
          </div>
        </div>

        <div class="mt-5 flex items-center gap-3">
          <button type="button" (click)="requestSync()" [disabled]="!canRun() || requesting()"
            class="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
            @if (requesting()) {
              <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
              Sending...
            } @else {
              Send Sync Request
            }
          </button>

          @if (!organizationId) {
            <p class="text-xs text-red-500">Select an organization.</p>
          } @else if (sourceEnvironment === destinationEnvironment) {
            <p class="text-xs text-red-500">Source and destination must be different.</p>
          } @else if (entities.length === 0) {
            <p class="text-xs text-red-500">Select at least one entity to include.</p>
          }
        </div>
      </div>

      <!-- History -->
      <div class="p-5">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-200">Sync History</h3>
          <div class="flex flex-wrap items-center gap-2">
            <label class="text-xs text-gray-500 dark:text-gray-400">Per page</label>
            <select
              [(ngModel)]="historyPerPage"
              (change)="onPerPageChange()"
              class="h-8 rounded-lg border border-gray-300 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            >
              @for (opt of perPageOptions; track opt) {
                <option [ngValue]="opt">{{ opt }}</option>
              }
            </select>
            <button type="button" (click)="loadHistory()" [disabled]="loadingHistory()"
              title="Refresh"
              class="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" [class.animate-spin]="loadingHistory()" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>
        </div>

        @if (historyError()) {
          <p class="mb-3 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{{ historyError() }}</p>
        }

        @if (loadingHistory()) {
          <div class="flex justify-center py-10">
            <svg class="h-6 w-6 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
          </div>
        } @else if (historyItems().length === 0) {
          <p class="py-10 text-center text-sm text-gray-400">No sync history found.</p>
        } @else {
          <div class="overflow-x-auto rounded-lg border border-gray-300 dark:border-gray-700">
            <table class="w-full text-left text-sm text-gray-700 dark:text-gray-300">
              <thead class="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                <tr>
                  <th class="px-4 py-2.5">Batch ID</th>
                  <th class="px-4 py-2.5">Direction</th>
                  <th class="px-4 py-2.5">Export</th>
                  <th class="px-4 py-2.5">Import</th>
                  <th class="px-4 py-2.5">Finished</th>
                  <th class="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                @for (item of historyItems(); track item.batchId) {
                  <tr class="border-t border-gray-200 dark:border-gray-800">
                    <td class="px-4 py-2.5 font-mono text-xs text-gray-500">{{ item.batchId }}</td>
                    <td class="px-4 py-2.5">
                      <span class="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-bold uppercase text-blue-700">{{ item.sourceEnvironment }}</span>
                      &rarr;
                      <span class="rounded bg-purple-100 px-1.5 py-0.5 text-xs font-bold uppercase text-purple-700">{{ item.destinationEnvironment }}</span>
                    </td>
                    <td class="px-4 py-2.5">
                      <div class="flex flex-wrap gap-1">
                        @for (detail of item.export; track detail.entity) {
                          @let status = exportItemStatus(detail);
                          <span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" [title]="exportDetailTooltip(detail)"
                            [class.bg-green-100]="status === 'complete'" [class.text-green-700]="status === 'complete'"
                            [class.bg-red-100]="status === 'failed'" [class.text-red-700]="status === 'failed'"
                            [class.bg-gray-100]="status === 'in-progress'" [class.text-gray-600]="status === 'in-progress'">
                            @if (status === 'in-progress') {
                              <span class="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-gray-400"></span>
                            }
                            {{ formatEntity(detail.entity) }}
                          </span>
                        } @empty {
                          <span class="text-xs text-gray-300">—</span>
                        }
                      </div>
                      @if (exportStartedAt(item); as started) {
                        <p class="mt-1.5 text-[11px] text-gray-400">
                          Started {{ started | date:'yyyy-MM-dd HH:mm' }}
                          @if (exportCompletedAt(item); as done) {
                            &middot; took {{ duration(started, done) }}
                          } @else {
                            &middot; running&hellip;
                          }
                        </p>
                      }
                    </td>
                    <td class="px-4 py-2.5">
                      <span class="mb-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium border border-dashed"
                        [class.bg-red-100]="hasImportFailures(item)" [class.text-red-700]="hasImportFailures(item)" [class.border-red-700]="hasImportFailures(item)"
                        [class.bg-green-100]="!hasImportFailures(item) && item.importStatus === 'complete'" [class.text-green-700]="!hasImportFailures(item) && item.importStatus === 'complete'" [class.border-green-700]="!hasImportFailures(item) && item.importStatus === 'complete'"
                        [class.bg-blue-100]="!hasImportFailures(item) && item.importStatus === 'in progress'" [class.text-blue-700]="!hasImportFailures(item) && item.importStatus === 'in progress'" [class.border-blue-700]="!hasImportFailures(item) && item.importStatus === 'in progress'"
                        [class.bg-gray-100]="item.importStatus === null" [class.text-gray-500]="item.importStatus === null" [class.border-gray-600]="item.importStatus === null">
                        {{ importStatusLabel(item) }}
                      </span>
                      <div class="flex flex-wrap gap-1">
                        @for (detail of item.import; track detail.entity) {
                          @let status = importItemStatus(detail);
                          <span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" [title]="importDetailTooltip(detail)"
                            [class.bg-green-100]="status === 'complete'" [class.text-green-700]="status === 'complete'"
                            [class.bg-red-100]="status === 'failed'" [class.text-red-700]="status === 'failed'"
                            [class.bg-gray-100]="status === 'skipped'" [class.text-gray-500]="status === 'skipped'"
                            [class.bg-blue-100]="status === 'in-progress'" [class.text-blue-700]="status === 'in-progress'">
                            @if (status === 'in-progress') {
                              <span class="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-blue-500"></span>
                            }
                            {{ formatEntity(detail.entity) }}
                          </span>
                        }
                      </div>
                      @if (importStartedAt(item); as started) {
                        <p class="mt-1.5 text-[11px] text-gray-400">
                          Started {{ started | date:'yyyy-MM-dd HH:mm' }}
                          @if (importCompletedAt(item); as done) {
                            &middot; took {{ duration(started, done) }}
                          } @else {
                            &middot; running&hellip;
                          }
                        </p>
                      }
                    </td>
                    <td class="px-4 py-2.5 text-xs">
                      @if (finishedAt(item); as done) {
                        {{ done | date:'yyyy-MM-dd HH:mm' }}
                      } @else {
                        <span class="text-gray-300">&mdash;</span>
                      }
                    </td>
                    <td class="px-4 py-2.5 text-right">
                      @if (item.importStatus === null) {
                        @if (hasExportFailures(item)) {
                          <span class="text-xs font-medium text-red-600">Export failed — cannot import</span>
                        } @else if (isExportComplete(item)) {
                          <button type="button" (click)="requestImport(item)"
                            [disabled]="!!importingBatchId()"
                            class="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                            @if (importingBatchId() === item.batchId) {
                              <svg class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                              Importing...
                            } @else {
                              Import
                            }
                          </button>
                        }
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>{{ totalCount() }} result{{ totalCount() === 1 ? '' : 's' }} &mdash; page {{ currentPage() }} of {{ totalPages }}</span>
            <div class="flex gap-1">
              <button type="button" (click)="changePage(currentPage() - 1)"
                [disabled]="currentPage() === 1"
                class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-700 dark:hover:bg-gray-800">
                Prev
              </button>
              <button type="button" (click)="changePage(currentPage() + 1)"
                [disabled]="currentPage() === totalPages"
                class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-700 dark:hover:bg-gray-800">
                Next
              </button>
            </div>
          </div>
        }
      </div>

    </div>
  </div>
</div>

@if (result(); as r) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="closeResult()">
  <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    @if (r.success) {
      <div class="flex items-center gap-2">
        <svg class="h-5 w-5 shrink-0 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        <h3 class="text-base font-semibold text-gray-800 dark:text-white">{{ r.details.direction }} request accepted</h3>
      </div>
      <dl class="mt-4 space-y-2 text-sm">
        <div class="flex justify-between gap-3"><dt class="text-gray-500 dark:text-gray-400">Batch ID</dt><dd class="font-mono text-xs text-gray-800 dark:text-gray-200 break-all text-right">{{ r.details.batchId }}</dd></div>
        <div class="flex justify-between gap-3"><dt class="text-gray-500 dark:text-gray-400">Organization</dt><dd class="font-mono text-xs text-gray-800 dark:text-gray-200 break-all text-right">{{ r.details.organizationId }}</dd></div>
        <div class="flex justify-between gap-3"><dt class="text-gray-500 dark:text-gray-400">Source</dt><dd class="uppercase font-medium text-gray-800 dark:text-gray-200">{{ r.details.source }}</dd></div>
        <div class="flex justify-between gap-3"><dt class="text-gray-500 dark:text-gray-400">Destination</dt><dd class="uppercase font-medium text-gray-800 dark:text-gray-200">{{ r.details.destination }}</dd></div>
        <div class="flex justify-between gap-3"><dt class="text-gray-500 dark:text-gray-400">Security hash</dt><dd class="font-mono text-xs text-gray-800 dark:text-gray-200 break-all text-right">{{ r.details.securityHashCode }}</dd></div>
        <div class="flex justify-between gap-3"><dt class="text-gray-500 dark:text-gray-400">Generated on</dt><dd class="text-gray-800 dark:text-gray-200">{{ r.details.generatedOn | date:'yyyy-MM-dd HH:mm:ss' }}</dd></div>
      </dl>
    } @else {
      <div class="flex items-center gap-2">
        <svg class="h-5 w-5 shrink-0 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <h3 class="text-base font-semibold text-gray-800 dark:text-white">Sync request failed</h3>
      </div>
      <p class="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">{{ r.error }}</p>
    }
    <div class="mt-5 flex justify-end">
      <button type="button" (click)="closeResult()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Close
      </button>
    </div>
  </div>
</div>
}
